// Przełącznik segmentowy (SG-1..SG-3) z PRZESUWAJĄCYM się wskaźnikiem (M-1):
// jeden wspólny wskaźnik jedzie nad aktywną opcją (nie crossfade). Równe segmenty.
export type SegOption = { value: string; label: string };

// Zakładka i jej panel muszą wskazywać na siebie nawzajem - inaczej czytnik ekranu ogłosi jedno
// i drugie, ale nie powie, że ta treść należy do tej zakładki. Obie strony liczą identyfikatory
// z tej samej reguły: wołający podaje wspólny rdzeń (useId), a te dwie funkcje dokładają resztę.
export const segTabId = (idBase: string, value: string) => `${idBase}-tab-${value}`;
export const segPanelId = (idBase: string, value: string) => `${idBase}-panel-${value}`;

export function SegmentedControl({
  idBase,
  options,
  value,
  onChange,
  ariaLabel,
}: {
  /** Wspólny rdzeń identyfikatorów - u wołającego `useId()`. Tym samym rdzeniem opisuje on panel. */
  idBase: string;
  options: SegOption[];
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
}) {
  const activeIndex = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div
      className="seg"
      role="tablist"
      aria-label={ariaLabel}
      style={{ ["--seg-count" as string]: options.length, ["--seg-active" as string]: activeIndex }}
    >
      <div className="seg__indicator" aria-hidden="true" />
      {options.map((o, i) => {
        // Celowo nie `o.value === value`: gdy wartość nie pasuje do żadnej opcji, wskaźnik i tak
        // stoi nad pierwszą (activeIndex), więc niech pierwsza będzie wtedy wybrana także dla
        // czytnika - inaczej żadna zakładka nie trafiłaby w kolejność Tab i lista stałaby się
        // nieosiągalna z klawiatury.
        const active = i === activeIndex;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            id={segTabId(idBase, o.value)}
            aria-selected={active}
            // Panele nieaktywnych zakładek są odmontowane, więc nie ma czego wskazywać -
            // aria-controls z martwym identyfikatorem byłby gorszy niż jego brak.
            aria-controls={active ? segPanelId(idBase, o.value) : undefined}
            // Roving tabindex: w kolejności Tab siedzi tylko wybrana zakładka, po pozostałych
            // chodzi się strzałkami. Dzięki temu Tab przeskakuje całą listę jednym naciśnięciem.
            tabIndex={active ? 0 : -1}
            className="seg__btn"
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              const list = e.currentTarget.parentElement;
              const go = (n: number) => {
                e.preventDefault();
                const next = (n + options.length) % options.length;
                onChange(options[next].value);
                // Fokus musi iść za wyborem. Bez tego zostaje na starym przycisku, kolejna
                // strzałka liczy sąsiada wciąż od niego i przełącznik staje w miejscu.
                const tabs = list?.querySelectorAll('[role="tab"]');
                (tabs?.[next] as HTMLElement | undefined)?.focus();
              };
              if (e.key === "ArrowRight" || e.key === "ArrowDown") go(i + 1);
              else if (e.key === "ArrowLeft" || e.key === "ArrowUp") go(i - 1);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
